import {
  AlertCircle,
  Edit,
  Eye,
  EyeOff,
  GripVertical,
  Plus,
  Settings,
  Trash2,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Configuration, DropdownOption } from '../../services/configurationService';
import { dropdownAdminService } from '../../services/dropdownAdminService';
import { Input } from '../forms';

interface OptionsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  config: Configuration;
  editingOption?: DropdownOption;
}

interface EditingOptionData {
  value: string;
  label: string;
  metadata: Record<string, any>;
  isActive: boolean;
  order: number;
}

const OptionsDrawer: React.FC<OptionsDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  config,
  editingOption,
}) => {
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<EditingOptionData>({
    value: '',
    label: '',
    metadata: {},
    isActive: true,
    order: 0,
  });
  const [addingNew, setAddingNew] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (config?.options) {
      setOptions([...config.options].sort((a, b) => a.order - b.order));
    }

    if (editingOption) {
      setEditingIndex(options.findIndex(opt => opt.value === editingOption.value));
      setEditingData({
        value: editingOption.value,
        label: editingOption.label,
        metadata: editingOption.metadata || {},
        isActive: editingOption.isActive,
        order: editingOption.order,
      });
    }
  }, [config, editingOption]);

  const resetEditingState = () => {
    setEditingIndex(null);
    setAddingNew(false);
    setEditingData({
      value: '',
      label: '',
      metadata: {},
      isActive: true,
      order: 0,
    });
    setErrors({});
  };

  const validateOption = (data: EditingOptionData, isNew: boolean) => {
    const newErrors: Record<string, string> = {};

    if (!data.value.trim()) {
      newErrors.value = 'Value is required';
    } else if (data.value.length > 100) {
      newErrors.value = 'Value must be less than 100 characters';
    } else if (isNew && options.some(opt => opt.value === data.value)) {
      newErrors.value = 'Value must be unique';
    }

    if (!data.label.trim()) {
      newErrors.label = 'Label is required';
    } else if (data.label.length > 200) {
      newErrors.label = 'Label must be less than 200 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStartEdit = (index: number) => {
    const option = options[index];
    setEditingIndex(index);
    setEditingData({
      value: option.value,
      label: option.label,
      metadata: option.metadata || {},
      isActive: option.isActive,
      order: option.order,
    });
    setAddingNew(false);
  };

  const handleStartAdd = () => {
    const maxOrder = Math.max(...options.map(opt => opt.order), 0);
    setAddingNew(true);
    setEditingIndex(null);
    setEditingData({
      value: '',
      label: '',
      metadata: {},
      isActive: true,
      order: maxOrder + 1,
    });
  };

  const handleSaveOption = async () => {
    if (!validateOption(editingData, addingNew)) return;

    setSaving(true);
    try {
      if (addingNew) {
        await dropdownAdminService.addDropdownOption(config.key, {
          value: editingData.value,
          label: editingData.label,
          metadata: editingData.metadata,
          order: editingData.order,
        });
      } else if (editingIndex !== null) {
        const originalOption = options[editingIndex];
        await dropdownAdminService.updateDropdownOption(config.key, originalOption.value, {
          label: editingData.label,
          metadata: editingData.metadata,
          isActive: editingData.isActive,
          order: editingData.order,
        });
      }

      await onSave();
      resetEditingState();
      setMessage({ type: 'success', text: 'Option saved successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving option:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save option' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOption = async (option: DropdownOption) => {
    if (!confirm(`Are you sure you want to delete "${option.label}"?`)) return;

    setSaving(true);
    try {
      await dropdownAdminService.deleteDropdownOption(config.key, option.value);
      await onSave();
      setMessage({ type: 'success', text: 'Option deleted successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error deleting option:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to delete option' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleOptionStatus = async (option: DropdownOption) => {
    setSaving(true);
    try {
      await dropdownAdminService.updateDropdownOption(config.key, option.value, {
        isActive: !option.isActive,
      });
      await onSave();
      setMessage({
        type: 'success',
        text: `Option ${!option.isActive ? 'activated' : 'deactivated'} successfully`,
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error toggling option status:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update option status' });
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      return;
    }

    const newOptions = [...options];
    const draggedOption = newOptions[draggedIndex];

    // Remove from old position
    newOptions.splice(draggedIndex, 1);

    // Insert at new position
    newOptions.splice(targetIndex, 0, draggedOption);

    // Update order values
    const reorderedOptions = newOptions.map((opt, index) => ({
      ...opt,
      order: index + 1,
    }));

    setOptions(reorderedOptions);
    setDraggedIndex(null);

    // Save new order to backend
    try {
      const optionValues = reorderedOptions.map(opt => opt.value);
      await dropdownAdminService.reorderDropdownOptions(config.key, optionValues);
      await onSave();
      setMessage({ type: 'success', text: 'Options reordered successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error reordering options:', error);
      setMessage({ type: 'error', text: 'Failed to save new order' });
      // Revert local changes
      if (config.options) {
        setOptions([...config.options].sort((a, b) => a.order - b.order));
      }
    }
  };

  const addMetadataField = () => {
    const key = prompt('Enter metadata key:');
    const value = prompt('Enter metadata value:');

    if (key && value) {
      setEditingData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          [key]: value,
        },
      }));
    }
  };

  const removeMetadataField = (key: string) => {
    setEditingData(prev => ({
      ...prev,
      metadata: Object.fromEntries(Object.entries(prev.metadata).filter(([k]) => k !== key)),
    }));
  };

  if (!isOpen) return null;

  const drawerContent = (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-[110]" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 w-full max-w-4xl bg-white shadow-xl rounded-xl border border-gray-200 z-[120] flex flex-col">
        {/* Sticky Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="bg-red-100 p-2 rounded-lg">
              <Settings className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Manage Options: {config.name}</h3>
              <p className="text-sm text-gray-600">
                Add, edit, reorder, and manage dropdown options
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Status Message */}
        {message && (
          <div
            className={`mx-6 mt-4 rounded-lg p-4 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">Options ({options.length})</h3>
            <button
              onClick={handleStartAdd}
              disabled={addingNew || editingIndex !== null}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Option
            </button>
          </div>

          {/* Add New Option Form */}
          {addingNew && (
            <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-gray-900 mb-4">Add New Option</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="Value"
                  value={editingData.value}
                  onChange={e => setEditingData(prev => ({ ...prev, value: e.target.value }))}
                  error={errors.value}
                  placeholder="unique-value"
                  hint="Unique identifier for this option"
                  required
                />
                <Input
                  label="Label"
                  value={editingData.label}
                  onChange={e => setEditingData(prev => ({ ...prev, label: e.target.value }))}
                  error={errors.label}
                  placeholder="Display Label"
                  hint="Text shown to users"
                  required
                />
              </div>

              {/* Metadata Section */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Metadata</label>
                  <button
                    type="button"
                    onClick={addMetadataField}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Add Field
                  </button>
                </div>
                {Object.entries(editingData.metadata).map(([key, value]) => (
                  <div key={key} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={key}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Key"
                      readOnly
                    />
                    <input
                      type="text"
                      value={value as string}
                      onChange={e =>
                        setEditingData(prev => ({
                          ...prev,
                          metadata: { ...prev.metadata, [key]: e.target.value },
                        }))
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Value"
                    />
                    <button
                      type="button"
                      onClick={() => removeMetadataField(key)}
                      className="p-2 text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={resetEditingState}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOption}
                  disabled={saving}
                  className="px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Add Option'}
                </button>
              </div>
            </div>
          )}

          {/* Options List */}
          <div className="space-y-2">
            {options.map((option, index) => (
              <div
                key={option.value}
                draggable={editingIndex !== index && !addingNew}
                onDragStart={e => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={e => handleDrop(e, index)}
                className={`bg-white border rounded-lg p-4 transition-all ${
                  draggedIndex === index ? 'opacity-50' : ''
                } ${editingIndex === index ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
              >
                {editingIndex === index ? (
                  // Edit Mode
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Edit Option</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <Input
                        label="Value"
                        value={editingData.value}
                        onChange={e => setEditingData(prev => ({ ...prev, value: e.target.value }))}
                        error={errors.value}
                        disabled
                        hint="Cannot change value after creation"
                      />
                      <Input
                        label="Label"
                        value={editingData.label}
                        onChange={e => setEditingData(prev => ({ ...prev, label: e.target.value }))}
                        error={errors.label}
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editingData.isActive}
                          onChange={e =>
                            setEditingData(prev => ({ ...prev, isActive: e.target.checked }))
                          }
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Active</span>
                      </label>
                    </div>

                    {/* Metadata Section */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">Metadata</label>
                        <button
                          type="button"
                          onClick={addMetadataField}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Add Field
                        </button>
                      </div>
                      {Object.entries(editingData.metadata).map(([key, value]) => (
                        <div key={key} className="flex items-center space-x-2 mb-2">
                          <input
                            type="text"
                            value={key}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Key"
                            readOnly
                          />
                          <input
                            type="text"
                            value={value as string}
                            onChange={e =>
                              setEditingData(prev => ({
                                ...prev,
                                metadata: { ...prev.metadata, [key]: e.target.value },
                              }))
                            }
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Value"
                          />
                          <button
                            type="button"
                            onClick={() => removeMetadataField(key)}
                            className="p-2 text-red-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={resetEditingState}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveOption}
                        disabled={saving}
                        className="px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="cursor-move text-gray-400 hover:text-gray-600">
                        <GripVertical className="h-5 w-5" />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-1">
                          <span className="font-medium text-gray-900">{option.label}</span>
                          <span className="text-sm text-gray-500">({option.value})</span>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              option.isActive
                                ? 'text-green-600 bg-green-50'
                                : 'text-gray-500 bg-gray-50'
                            }`}
                          >
                            {option.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>

                        {Object.keys(option.metadata || {}).length > 0 && (
                          <div className="text-sm text-gray-600">
                            Metadata: {Object.keys(option.metadata || {}).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleOptionStatus(option)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title={option.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {option.isActive ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>

                      <button
                        onClick={() => handleStartEdit(index)}
                        disabled={addingNew || editingIndex !== null}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => handleDeleteOption(option)}
                        disabled={saving}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {options.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">No options yet</p>
                <p className="text-sm">Add your first dropdown option to get started.</p>
              </div>
            )}
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-white rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(drawerContent, document.body);
};

export default OptionsDrawer;
